import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { wellnessFields, wellnessQuestionNumber } from './validation.js';
import { addMissingChartNoteStarters, defaultNoteLibrary, noteSlots, validateNoteTemplates, type NoteTemplateLibrary, type NoteTemplate } from './noteTemplates.js';

export function NoteTemplateManager() {
  const [library, setLibrary] = useState<NoteTemplateLibrary | null>(null);
  const [saved, setSaved] = useState('');
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let current = true;
    setBusy(true);
    void api.wellnessNoteTemplates().then((data) => {
      const next = { ...data, templates: validateNoteTemplates(data.templates) };
      if (current) { setLibrary(next); setSaved(JSON.stringify(next.templates)); setSelected(next.templates[0]?.id ?? ''); }
    }).catch(() => { if (current) setMessage('The template library could not be loaded.'); })
      .finally(() => { if (current) setBusy(false); });
    return () => { current = false; };
  }, [reload]);
  const dirty = library !== null && JSON.stringify(library.templates) !== saved;
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    const guard = (event: Event) => {
      if (dirty) { event.preventDefault(); setMessage('Publish your wording or reload the published copy before leaving.'); }
    };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('veritas:before-navigation', guard);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener('veritas:before-navigation', guard);
    };
  }, [dirty]);
  const current = library?.templates.find((item) => item.id === selected);
  const missingStarters = defaultNoteLibrary().templates.some((starter) => !library?.templates.some((item) => item.id === starter.id));
  function change(values: Partial<NoteTemplate>) {
    if (!library || busy) return;
    setLibrary({ ...library, templates: library.templates.map((item) => item.id === selected ? { ...item, ...values } : item) });
    setMessage('');
  }
  async function publish() {
    if (!library || busy) return;
    setMessage('');
    try {
      const templates = validateNoteTemplates(library.templates);
      setBusy(true);
      const result = await api.saveWellnessNoteTemplates({ baseRevision: library.revision, templates });
      setLibrary(result); setSaved(JSON.stringify(result.templates));
      setMessage('Published. Newly opened forms use this wording. Existing notes have not been changed.');
    } catch (error) {
      setMessage(error instanceof ApiError && error.status === 409
        ? 'Another manager published changes. Your edits remain here. Reload the published copy only when you are ready to replace them.'
        : error instanceof ApiError && error.status === 403
        ? 'Publishing requires the organization-wide Manager or Administrator role and fresh security-key verification. Verify access and try again.'
        : error instanceof Error ? error.message : 'The wording could not be published.');
    } finally { setBusy(false); }
  }
  return <section className="card" aria-labelledby="note-library-title">
    <h2 id="note-library-title" className="text-h3">Standard note wording</h2>
    <p className="mt-2 text-sm">Maintain consistent chart-style notes for field staff. Use reusable wording, never names, case numbers, or other personal details.</p>
    <p className="mt-2 text-sm">Field managers use the Manager role to maintain this shared library. Publishing requires organization-wide access and security-key verification. Staff can edit their own inserted notes without changing these templates.</p>
    <p className="mt-2 text-sm">Use a detail such as {'{{observation}}'} where staff must enter facts. They can edit inserted text. Disabled templates disappear from new selections, without changing saved records.</p>
    {library && <p className="mt-2 text-sm">Revision {library.revision}{dirty ? ' · Unpublished changes' : ' · Published copy'}</p>}
    {message && <p className="mt-3 text-sm" role="status">{message}</p>}
    <div className="my-4 flex flex-wrap gap-2">
      <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => {
        if (dirty && !window.confirm('Replace unpublished edits with the shared published library?')) return;
        setReload((value) => value + 1);
      }}>Reload published copy</button>
      <button type="button" className="btn btn-secondary" disabled={!library || busy || library.templates.length >= 40}
        onClick={() => {
          if (!library) return;
          const id = crypto.randomUUID();
          setLibrary({ ...library, templates: [...library.templates, { id, label: 'New wording', text: '', field: '*', enabled: false }] });
          setSelected(id); setMessage('');
        }}>Add wording</button>
      <button type="button" className="btn btn-secondary" disabled={!library || busy || !missingStarters}
        onClick={() => {
          if (!library) return;
          try {
            const templates = addMissingChartNoteStarters(library.templates);
            setLibrary({ ...library, templates });
            if (!selected) setSelected(templates[0]?.id ?? '');
            setMessage('Missing chart-note starters added for review. Existing wording and disabled entries were preserved. Publish when ready.');
          } catch (error) { setMessage(error instanceof Error ? error.message : 'The starters could not be added.'); }
        }}>Add chart-note starters</button>
    </div>
    {library && <form onSubmit={(event) => { event.preventDefault(); void publish(); }}>
      <label className="field" htmlFor="note-library-select">Template
        <select id="note-library-select" className="input" value={selected} disabled={busy}
          onChange={(event) => setSelected(event.target.value)}>
          {!library.templates.length && <option value="">No templates</option>}
          {library.templates.map((item) => <option value={item.id} key={item.id}>{item.label}{item.enabled ? '' : ' (disabled)'}</option>)}
        </select>
      </label>
      {current && <div className="mt-4 space-y-4">
        <label className="field" htmlFor="note-template-label">Button label
          <input id="note-template-label" className="input" value={current.label} maxLength={80} required disabled={busy}
            onChange={(event) => change({ label: event.target.value })} />
        </label>
        <label className="field" htmlFor="note-template-field">Show beside
          <select id="note-template-field" className="input" value={current.field} disabled={busy}
            onChange={(event) => change({ field: event.target.value })}>
            <option value="*">Every narrative question</option>
            {wellnessFields.filter((field) => field.kind === 'textarea').map((field) =>
              <option key={field.name} value={field.name}>Question {wellnessQuestionNumber(field)}: {field.label}</option>)}
          </select>
        </label>
        <label className="field" htmlFor="note-template-text">Reusable wording
          <textarea id="note-template-text" className="input min-h-[180px]" value={current.text} maxLength={1000} required disabled={busy}
            onChange={(event) => change({ text: event.target.value })} />
        </label>
        <div className="rounded border border-neutral-300 bg-neutral-100 p-3" aria-label="Staff wording preview">
          <h3 className="font-semibold">Staff preview: {current.label}</h3>
          <p className="mt-2 whitespace-pre-wrap break-words text-sm">{current.text || 'Enter wording to preview it here.'}</p>
          <p className="mt-2 text-sm">{noteSlots(current.text).length
            ? `${noteSlots(current.text).length} details will be requested before insertion.`
            : 'One tap inserts this wording. Staff must confirm it matches the visit.'}</p>
        </div>
        <label className="flex items-center gap-3 min-h-[44px]">
          <input type="checkbox" checked={current.enabled} disabled={busy} onChange={(event) => change({ enabled: event.target.checked })} />
          Available to staff when published
        </label>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => {
          if (!window.confirm('Remove this template from the library? Existing notes will not change.')) return;
          const templates = library.templates.filter((item) => item.id !== selected);
          setLibrary({ ...library, templates }); setSelected(templates[0]?.id ?? '');
        }}>Remove wording</button>
      </div>}
      <button type="submit" className="btn btn-primary mt-5" disabled={busy || !dirty}>{busy ? 'Saving…' : 'Publish wording'}</button>
    </form>}
  </section>;
}
